<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
      :hasBack="true"
  >
    <template #subHeaderActions>
      <MonthPicker
          class="me-3"
          id="payslip-month-picker"
          :current-date="query.date"
          @update:date="handleDateChange" />
    </template>
    <template #layoutContent>
      <Loader :is-loading="isLoading">
        <PageLoader v-if="isFetching" />
        <div class="row">
          <div class="col-12 col-md-3">
            <Card class="card p-4">
              <div class="row">
                <Input
                    container-class="me-3"
                    inputClass="form-control form-control-sm"
                    name="input-search"
                    :isInputGroup="true"
                    :placeholder="$t('label.search')"
                    v-model="query.search"
                >
                  <template #rightAddon>
                    <span class="position-absolute border-0 bg-transparent end-0 top-50 translate-middle">
                      <i class="fa fa-search"></i>
                    </span>
                  </template>
                </Input>
              </div>
              <div class="d-flex flex-column mt-5 h-600px scroll-y">
                <div class="col-12" @click.prevent="() => toggleSelectedUser(data)"
                     v-for="(data, index) in filteredEmployees" :key="index">
                  <div class="d-flex align-items-center p-4 cursor-pointer bg-hover-light-primary" :class="{ 'bg-light-primary' : selectedUser?.id === data.user.id }">
                    <AvatarBadge :avatar-url="data.user.avatar_url" :fullname="data.user.fullname" />
                    <span class="flex-grow-1 ms-4 fs-4"> {{ data.user.fullname  }} </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <div class="col-12 col-md-9">
            <Card class="h-650px" v-if="!selectedUser?.id">
              <div class="h-100 d-flex justify-content-center align-items-center" >
                <div class=" text-center ">
                  <div class="pt-8 ">
                    <Img filename="empty.png" alt=""
                         class="mw-100 h-125px h-sm-175px" />
                  </div>
                  <h2 class="fs-2x fw-bolder mb-0">{{ $t('payslip.noUserSelected') }}</h2>
                  <p class="mt-3 fs-4"> {{ $t('payslip.noUserSelectedDesc') }} </p>
                </div>
              </div>
            </Card>
            <PayslipForm
                v-show="selectedUser?.id"
                :key="selectedUser?.id"
                :user="selectedUser"
                :payslip="payslip"
                :currency="currency"
                :submit-form="createPayslip"
                :cancel="router.back"
                :is-loading="submitting"
            />
          </div>
        </div>
      </Loader>
    </template>
  </SubLayout>
</template>
<script setup>

import Card from "@/components/Card.vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useRoute, useRouter} from "vue-router";
import {useStore} from "vuex";
import {useLocales} from "@/composables/locales";
import {useSwal} from "@/composables/useSwal";
import {computed, onMounted, ref, watch} from "vue";
import Loader from "@/components/Loader.vue";
import AvatarBadge from "@/components/AvatarBadge.vue";
import Input from "@/components/Input.vue";
import PayslipForm from "@/Modules/RH/components/Payslip/PayslipForm.vue";
import MonthPicker from "@/components/MonthPicker.vue";
import PageLoader from "@/components/PageLoader.vue";
import Img from "@/components/Img.vue";

const router = useRouter()
const route = useRoute()
const store = useStore()
const {locale, datatableLocaleUrl} = useLocales()
const {showSwal} = useSwal()

const title = ref("breadcrumbs.payslip.title")
const breadcrumbs = ref([
  {
    name: 'breadcrumbs.payslip.list',
    route: 'payslips'
  },
  {
    name: 'breadcrumbs.payslip.new',
    route: 'payslips-office-create',
    params: {
      office: route.params.office,
    }
  },
])
const table = ref()
const isLoading = ref(false)
const isFetching = ref(false)
const submitting = ref(false)
const query = ref({
  status: '',
  type: '',
  date: new Date(),
  year: new Date().getFullYear(),
  month: String(new Date().getMonth() + 1).padStart(2, '0'),
  search: '',
  office: ''
})
const selectedUser = ref({
  fullName: ''
})
const payslip = ref({
  payslip_id: "",
  office_id: "",
  user_id: "",
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  base_salary: "0",
  allowances: [{
    id: "",
    value: 1,
    edited: false
  }],
  others: [{
    id: "",
    value: 1,
    edited: false
  }],
  pensions: [{
    id: "",
    value: 1,
    edited: false
  }],
  taxes: [{
    id: "",
    value: 1,
    edited: false
  }],
  pension_employers: [{
    id: "",
    value: 1,
    edited: false
  }],
  taxe_employers: [{
    id: "",
    value: 1,
    edited: false
  }],
})
const currency = ref("")

const offices = computed(() => store.getters['payslip/payslipCompanyOffices'])
const employeesPayslips = computed(() => store.getters['payslip/currentEmployeePayslip'])
const currentCompany = computed(() => store.getters['offices/getSelectedCompany'])
const filteredEmployees = computed(() => {
  if(!query.value.search) return employeesPayslips.value?.payslips
  return employeesPayslips.value?.payslips?.filter(payslip => payslip?.user.fullname.toString().toLowerCase().includes(query.value.search.toString().toLowerCase()))
})

watch(() => currentCompany.value?.id, () => {
  getPayslipCompanyOffices()
})

watch(() => offices.value, (value) => {
  if(value.length) {
    query.value.office = value[0]?.id
    router.replace({
      name: route.name,
      params: { ...route.params, office: value[0]?.id },
      query: {
        month: query.value.month,
        year: query.value.year,
        status: query.value.status,
      }
    })
    selectedUser.value = null
    payslip.value.user_id = ''
    payslip.value.month = 1
    payslip.value.year = 2000
    payslip.value.base_salary = '0'
    payslip.value.allowances = []
    payslip.value.others = []
    payslip.value.pensions = []
    payslip.value.taxes = []
    payslip.value.pension_employers = []
    payslip.value.taxe_employers = []
    currency.value = ''
  }
}, {deep: true })

watch(() => query.value, () => {
  getCurrentEmployeePayslip()
}, {deep: true })

watch(() => filteredEmployees.value, () => {
  if(filteredEmployees.value?.length) {
    toggleSelectedUser(filteredEmployees.value[0])
  }
})


const toggleSelectedUser = (data) => {
  selectedUser.value = data?.user
  payslip.value.user_id = data?.user?.id
  payslip.value.month = data?.month
  payslip.value.year = data?.year
  payslip.value.base_salary = data?.base_salary
  payslip.value.allowances =  data?.allowances || []
  payslip.value.others = data?.others || []
  payslip.value.pensions = data?.pensions || []
  payslip.value.taxes = data?.taxes || []
  payslip.value.pension_employers = data?.pension_employers || []
  payslip.value.taxe_employers = data?.taxe_employers || []
  currency.value = data?.currency_symbol
  router.replace({
    name: route.name,
    params: route.params,
    query: { ...route.query, user: data?.user?.id }
  })

}

const getCurrentEmployeePayslip = () => {
  if(query.value.year && query.value.month && query.value.office) {
    isFetching.value = true
    store.dispatch('payslip/getCurrentEmployeePayslip', {
      year: query.value.year,
      month: query.value.month,
      office_id: query.value.office,
      status: ''
    }).finally(() => isFetching.value = false)
  }
}

const handleDateChange = (date) => {
  query.value.year = date.year
  query.value.month = date.month
  query.value.date = date.date
  router.replace({
    name: route.name,
    params: { ...route.params },
    query: {...route.query, year: date.year, month: date.month},
  })
}

const createPayslip = () => {
  submitting.value = true
  store.dispatch("payslip/storePayslip", {...payslip.value, office_id: route.params.office})
      .then(res => {
        window.toastr['success'](i18n.global.t('payslip.notification.payslip_saved'))
        getCurrentEmployeePayslip()
        selectedUser.value = null
      })
      .catch()
      .finally(() => submitting.value = false)
}

function getPayslipCompanyOffices() {
  if(currentCompany.value?.id) {
    isLoading.value = true
    store.dispatch('payslip/getPayslipCompanyOffices', {
      year:  query.value.year,
      month: query.value.month,
      society_id: currentCompany.value?.id,
    })
        .then(res => {

        })
        .catch()
        .finally(() => isLoading.value = false)
  }
}

const loadData = () => {
  query.value.office = route.params.office
  query.value.year = route.query.year
  query.value.month = route.query.month
  query.value.date = new Date(`${route.query.year}-${route.query.month}-01`)
  getPayslipCompanyOffices()
  getCurrentEmployeePayslip()
}

onMounted(() => {
  loadData()
})

</script>


<style scoped>

</style>